import React, { useRef, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';

/**
 * SpineItem — drawn as a physical 3D game case, no CSS 3D transforms needed.
 *
 * Three explicitly drawn faces (like the reference render):
 *
 *  ┌──────────────────┐  ← TOP FACE  (warm-light left catch, dark surface)
 *  │                  │
 *  │   FRONT FACE     │ ▐  ← RIGHT SIDE FACE (dark, in shadow)
 *  │   (spine art)    │ ▐
 *  │                  │ ▐
 *  └──────────────────┘ ▐
 *         ↓ ↓ shadow on shelf
 *
 * The 3D illusion is entirely perceptual:
 *   - Top face tells the brain "this is a box, not a flat picture"
 *   - Dark right side suggests the case has thickness and a shadowed face
 *   - Two-layer drop-shadow (tight contact + wide ambient) grounds the item
 */

const TOP_H  = 20; // visible height of the top face (px) — how far you're looking down
const SIDE_W = 10; // width of the right-side face (px) — how thick the case looks

const SpineItem = ({ item, onClick, lazy = true }) => {
  const spineRef  = useRef(null);
  const [isVisible, setIsVisible] = useState(!lazy);

  const titleLength = (item.name || item.title || '').length;
  const spineWidth  = Math.min(46, Math.max(22, 22 + titleLength * 0.4));

  useEffect(() => {
    if (!lazy || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { rootMargin: '300px', threshold: 0.01 }
    );
    if (spineRef.current) observer.observe(spineRef.current);
    return () => observer.disconnect();
  }, [lazy, isVisible]);

  const getImageUrl = () => {
    const url = item.imageUrl || item.coverUrl || '';
    if (!url) return '';
    if (url.includes('images.igdb.com'))
      return url.replace('/t_cover_big/', '/t_cover_small/').replace('/t_1080p/', '/t_cover_small/');
    return url;
  };
  const imageUrl  = getImageUrl();

  const getBaseColor = () => {
    const p = (item.platform || '').toLowerCase();
    if (p.includes('playstation') || p.includes('ps')) return '#00348c';
    if (p.includes('xbox'))  return '#0e5c0e';
    if (p.includes('nintendo') || p.includes('switch') || p.includes('wii') || p.includes('game boy') || p.includes('gameboy')) return '#c40012';
    if (p.includes('sega') || p.includes('mega drive') || p.includes('dreamcast') || p.includes('saturn')) return '#17488d';
    if (p.includes('pc') || p.includes('windows')) return '#1a1a2e';
    if (p.includes('atari')) return '#d4a017';
    return '#2c2c3e';
  };

  const getAccentColor = () => {
    const p = (item.platform || '').toLowerCase();
    if (p.includes('playstation') || p.includes('ps')) return '#0070d1';
    if (p.includes('xbox'))  return '#52b043';
    if (p.includes('nintendo') || p.includes('switch') || p.includes('wii')) return '#ff3b3b';
    if (p.includes('sega'))  return '#4d8cff';
    if (p.includes('pc') || p.includes('windows')) return '#9146ff';
    if (p.includes('atari')) return '#f5c518';
    return '#888';
  };

  const title       = item.name || item.title || 'Unknown';
  const baseColor   = getBaseColor();
  const accentColor = getAccentColor();

  return (
    <Tooltip title={title} placement="top" arrow enterDelay={400}>
      <Box
        ref={spineRef}
        onClick={() => onClick?.(item)}
        sx={{
          // Outer wrapper sized to contain all three faces
          width:  `${spineWidth + SIDE_W}px`,
          height: `${200 + TOP_H}px`,
          position: 'relative',
          flexShrink: 0,
          userSelect: 'none',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, filter 0.2s ease',
          // Two-layer shadow: tight contact shadow + wide ambient shadow on shelf
          // Real objects on surfaces always have both — a single blur looks painted-on
          filter: [
            'drop-shadow(0px 3px 5px rgba(0,0,0,0.98))',
            'drop-shadow(0px 16px 24px rgba(0,0,0,0.85))',
          ].join(' '),
          '&:hover': {
            transform: 'translateY(-14px)',
            filter: [
              'drop-shadow(0px 5px 8px rgba(0,0,0,1))',
              'drop-shadow(0px 28px 28px rgba(0,0,0,0.92))',
            ].join(' '),
            zIndex: 50,
          },
        }}
      >
        {/*
          ── TOP FACE ────────────────────────────────────────────────────────
          The single most important cue that tells the brain "this is a box."
          In the reference image you clearly see the dark top surface of the case.

          Dark base with a warm amber glow on the left edge — the shelf's warm
          overhead light catches the top-left corner of each case.
          A thin bright hairline at the very top edge simulates specular highlight.
        */}
        <Box sx={{
          position: 'absolute',
          top: 0, left: 0,
          width: `${spineWidth}px`,
          height: `${TOP_H}px`,
          // Left-edge warm catch light fading to dark across the top surface
          background: 'linear-gradient(to right, rgba(200,140,60,0.6) 0%, rgba(60,40,20,0.9) 40%, rgba(20,14,8,0.95) 100%)',
          backgroundColor: '#1a1208',
          borderRadius: '1px 1px 0 0',
          // Specular highlight on the very top edge
          borderTop: '1px solid rgba(210,160,70,0.3)',
        }} />

        {/*
          ── RIGHT SIDE FACE ─────────────────────────────────────────────────
          A narrow dark strip to the right of the front face.
          This represents the right side of the physical case — in shadow because
          the light comes from the upper-left in the reference images.

          Slight gradient: slightly lighter at top (top-right corner catches some
          ambient light from above) darkening toward the bottom.
        */}
        <Box sx={{
          position: 'absolute',
          top: `${TOP_H}px`,
          left: `${spineWidth}px`,
          width: `${SIDE_W}px`,
          height: '200px',
          overflow: 'hidden',
          backgroundColor: baseColor,
          backgroundImage: isVisible && imageUrl ? `url(${imageUrl})` : 'none',
          backgroundSize: 'auto 100%',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
        }}>
          {/* Dark overlay so side face is shadowed */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.75) 100%)',
          }} />
        </Box>

        {/*
          ── FRONT FACE (the spine) ──────────────────────────────────────────
          Right-edge slice of cover art (physically correct — the spine bleeds off
          the right side of the front cover artwork).
        */}
        <Box sx={{
          position: 'absolute',
          top: `${TOP_H}px`,
          left: 0,
          width: `${spineWidth}px`,
          height: '200px',
          overflow: 'hidden',
          backgroundColor: baseColor,
          backgroundImage: isVisible && imageUrl ? `url(${imageUrl})` : 'none',
          backgroundSize: 'auto 100%',
          backgroundPosition: 'right center',
          backgroundRepeat: 'no-repeat',
          borderRadius: '0 0 1px 0',
        }}>
          {/* Readability overlay — heavier on left where text sits */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: imageUrl && isVisible
              ? 'linear-gradient(to right, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.05) 100%)'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.08))',
          }} />

          {/* Left binding shadow */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, width: '5px', height: '100%',
            background: 'linear-gradient(to right, rgba(0,0,0,0.7), transparent)',
            pointerEvents: 'none',
          }} />

          {/* Vertical title */}
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px 4px 12px 6px',
          }}>
            <Box component="span" sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              fontFamily: '"Roboto Condensed", "Arial Narrow", Roboto, Arial, sans-serif',
              letterSpacing: '0.03em',
              lineHeight: 1.1,
              textShadow: '0 1px 4px rgba(0,0,0,1), 0 0 10px rgba(0,0,0,0.9)',
              overflow: 'hidden',
              maxHeight: '100%',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}>
              {title}
            </Box>
          </Box>

          {/* Platform accent bar */}
          <Box sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '4px', backgroundColor: accentColor, opacity: 0.9,
          }} />

          {/* Right edge specular highlight */}
          <Box sx={{
            position: 'absolute', top: 0, right: 0, width: '1px', height: '100%',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
            pointerEvents: 'none',
          }} />
        </Box>
      </Box>
    </Tooltip>
  );
};

export default SpineItem;
